import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useOrg } from '../contexts/OrgContext'
import * as api from '../api/client'

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const { refreshOrganizations, setCurrentOrg } = useOrg()

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState('')
  const [orgName, setOrgName] = useState('')

  useEffect(() => {
    acceptInvite()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-lint baseline: deps intentionally omitted
  }, [token])

  const acceptInvite = async () => {
    if (!token) {
      setStatus('error')
      setError('Invalid invite link')
      return
    }

    try {
      const response = await api.acceptOrgInvite(token)
      setStatus('success')

      // Refresh organizations and switch to the new org
      await refreshOrganizations()

      // Get the org details to show in success message
      if (response.membership?.org_id) {
        try {
          const org = await api.getOrganization(response.membership.org_id)
          setOrgName(org.name)
          setCurrentOrg(org)
        } catch {
          // Ignore error getting org name
        }
      }
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Failed to accept invite')
    }
  }

  return (
    <div className="max-w-md mx-auto mt-12">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Accepting invite...</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">Please wait while we process your invitation.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Welcome to the team!</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              You've successfully joined{orgName ? ` ${orgName}` : ' the organization'}.
            </p>
            <div className="mt-6 space-x-3">
              <Link
                to="/organizations"
                className="inline-block px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                View Organizations
              </Link>
              <Link
                to="/"
                className="inline-block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              >
                Go to Dashboard
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Unable to accept invite</h2>
            <p className="text-red-600 dark:text-red-400 mt-2">{error}</p>
            <p className="text-gray-500 dark:text-gray-400 mt-4 text-sm">
              The invite may have expired or already been used. Please contact the organization admin for a new invite.
            </p>
            <div className="mt-6">
              <Link
                to="/"
                className="inline-block px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Go to Dashboard
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
